// ============================================================================
// PEÇA STANDALONE VIEWER
// ARQUIVO: src/features/files/PecaStandaloneViewer.jsx
// RESPONSABILIDADE: Fase C5 (02/09/2026, pedido do titular) — "pop-out" de
//   A Peça em Si numa janela própria de verdade (window.open), igual Gmail/
//   Google Docs fazem, e igual o Portal já faz pra Anexos em /view/:id
//   (DocumentViewer.jsx) — mesmo padrão, adaptado pra conteúdo interno
//   (exige login, ao contrário de /view/:id que é público de propósito).
//   Pesquisa de mercado feita antes de construir (02/09/2026): Document
//   Picture-in-Picture API (Chrome/Firefox, pensada pra widget flutuante
//   pequeno, não pra ler documento inteiro) e Window Management API
//   (só Chromium, exige permissão extra, só vale a pena quando "colocar
//   automaticamente no monitor X" é o objetivo) foram descartadas — o
//   padrão universal pra "essa tela específica numa janela real que dá pra
//   arrastar" é rota própria + window.open, sem dependência de API nova.
//
// Bootstraps sozinho a partir da URL (/peca/:workspaceId/:pieceId) — não
// depende de nenhum estado do CadernoCockpit já estar montado, porque a
// janela pop-out é um browsing context separado (window.open), com o
// próprio ciclo de vida do React do zero.
// ============================================================================

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { onSnapshot } from "firebase/firestore";
import { getWorkspaceDocRef } from "./CadernoCockpitRepository";
import { CadernoMarkdownViewer } from "./CadernoMarkdownViewer";
import { PecaRedlineView } from "./PecaRedlineView";

export default function PecaStandaloneViewer() {
  const { workspaceId, pieceId } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  // Modo inicial vem de ?modo=redline|normal (ver abrirPecaEmNovaAba.js).
  const [pecaViewMode, setPecaViewMode] = useState(() =>
    new URLSearchParams(window.location.search).get("modo") === "redline" ? "redline" : "normal",
  );

  useEffect(() => {
    const ref = getWorkspaceDocRef(workspaceId);
    if (!ref) {
      setLoading(false);
      return;
    }
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setWorkspace(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [workspaceId]);

  const peca = (Array.isArray(workspace?.pieces) ? workspace.pieces : []).find(
    (p) => String(p?.id || "") === String(pieceId || ""),
  );
  const driveWebViewLink = String(peca?.pecaMdWebViewLink || "").trim();
  const pieceLabel = String(peca?.pieceType || "").trim() || "Peça";

  useEffect(() => {
    // Modo no título: com Normal e Redline em abas separadas (e agrupadas),
    // a aba precisa dizer qual é qual — nunca só pela posição.
    document.title = `${pieceLabel} (${pecaViewMode === "redline" ? "Redline" : "Normal"}) — RDA`;
  }, [pieceLabel, pecaViewMode]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center gap-3 shrink-0">
        <i className="fas fa-file-lines text-[#C5A059]" aria-hidden="true"></i>
        <h1 className="text-base font-bold text-[#20243B] truncate">{pieceLabel}</h1>
        {driveWebViewLink ? (
          <div className="flex items-center gap-1 ml-2">
            <button
              type="button"
              onClick={() => setPecaViewMode("normal")}
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                pecaViewMode === "normal"
                  ? "border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]"
                  : "border-gray-200 text-gray-400 hover:text-gray-600"
              }`}
            >
              Normal
            </button>
            <button
              type="button"
              onClick={() => setPecaViewMode("redline")}
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md border ${
                pecaViewMode === "redline"
                  ? "border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]"
                  : "border-gray-200 text-gray-400 hover:text-gray-600"
              }`}
              title="O que mudou desde a última versão salva"
            >
              Redline
            </button>
          </div>
        ) : null}
      </div>
      <div className="flex-1 min-h-0 p-5">
        <div className="max-w-4xl mx-auto h-full">
          {loading ? (
            <div className="text-center text-xs text-gray-400 py-16">
              <i className="fas fa-spinner fa-spin mr-1.5"></i> Carregando...
            </div>
          ) : !driveWebViewLink ? (
            <div className="text-center text-xs text-gray-400 py-16">
              A peça em si ainda não foi criada para este documento.
            </div>
          ) : pecaViewMode === "redline" ? (
            <PecaRedlineView driveWebViewLink={driveWebViewLink} />
          ) : (
            <CadernoMarkdownViewer driveWebViewLink={driveWebViewLink} />
          )}
        </div>
      </div>
    </div>
  );
}
